import { useState, useCallback } from 'react';
import { Briefcase, CheckCircle2, ChevronLeft, ChevronRight, Upload, Calculator, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

function Field({ label, required, children }) {
    return (
        <div className="flex flex-col gap-1.5">
            <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                {label}{required ? <span className="text-rose-500 ml-0.5">*</span> : null}
            </Label>
            {children}
        </div>
    );
}

function FormInput({ id, type, label, placeholder, value, onChange, required }) {
    return (
        <Field label={label} required={required}>
            <Input id={id} type={type || 'text'} placeholder={placeholder} value={value} onChange={onChange} required={required}
                className="h-11 rounded-xl border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all" />
        </Field>
    );
}

function FormSelect({ id, label, options, value, onChange, required }) {
    return (
        <Field label={label} required={required}>
            <select id={id} value={value} onChange={onChange} required={required}
                className="h-11 rounded-xl border px-3 text-sm w-full outline-none border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all">
                <option value="">Select…</option>
                {options.map(function (o) { return <option key={o.value} value={o.value}>{o.label}</option>; })}
            </select>
        </Field>
    );
}

function FileUploadField({ label, accept, onChange, file }) {
    return (
        <div className="flex flex-col gap-1.5">
            <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">{label}</Label>
            <label className="flex flex-col items-center justify-center gap-2 h-24 border-2 border-dashed rounded-xl cursor-pointer border-slate-200 dark:border-slate-700 hover:border-indigo-400 bg-slate-50 dark:bg-slate-800/50 hover:bg-indigo-50/50 transition-all group">
                <input type="file" accept={accept} className="sr-only" onChange={onChange} />
                {file ? (
                    <div className="flex items-center gap-2 px-3">
                        <CheckCircle2 className="h-5 w-5 text-indigo-500 shrink-0" />
                        <span className="text-xs text-indigo-600 font-medium truncate max-w-[160px]">{file.name}</span>
                    </div>
                ) : (
                    <div className="flex flex-col items-center gap-1">
                        <Upload className="h-5 w-5 text-slate-400 group-hover:text-indigo-500 transition-colors" />
                        <span className="text-xs text-slate-500">Click to upload</span>
                    </div>
                )}
            </label>
        </div>
    );
}

function EMICalc() {
    var sa = useState(1000000); var amount = sa[0]; var setAmount = sa[1];
    var sr = useState(10.5); var rate = sr[0]; var setRate = sr[1];
    var st = useState(5); var tenure = st[0]; var setTenure = st[1];
    var emi = useCallback(function () {
        var r = rate / 12 / 100, n = tenure * 12;
        if (!amount || !r || !n) return 0;
        return (amount * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    }, [amount, rate, tenure]);
    var emiVal = emi(); var tp = emiVal * tenure * 12; var ti = tp - amount;
    function fmt(n) { return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n); }
    return (
        <div className="rounded-2xl border border-indigo-100 dark:border-indigo-900/40 bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/10 p-5 space-y-4">
            <div className="flex items-center gap-2"><Calculator className="h-4 w-4 text-indigo-600" /><h4 className="text-sm font-semibold text-indigo-700 dark:text-indigo-400">EMI Calculator</h4></div>
            <div className="space-y-3">
                <div className="space-y-1"><div className="flex justify-between text-xs text-slate-600 dark:text-slate-400"><span>Loan Amount</span><span className="font-medium">{fmt(amount)}</span></div><input type="range" min="100000" max="10000000" step="50000" value={amount} onChange={function (e) { setAmount(Number(e.target.value)); }} className="w-full accent-indigo-600 h-1.5 cursor-pointer" /></div>
                <div className="space-y-1"><div className="flex justify-between text-xs text-slate-600 dark:text-slate-400"><span>Interest Rate</span><span className="font-medium">{rate}% p.a.</span></div><input type="range" min="5" max="24" step="0.1" value={rate} onChange={function (e) { setRate(Number(e.target.value)); }} className="w-full accent-indigo-600 h-1.5 cursor-pointer" /></div>
                <div className="space-y-1"><div className="flex justify-between text-xs text-slate-600 dark:text-slate-400"><span>Tenure</span><span className="font-medium">{tenure} yrs</span></div><input type="range" min="1" max="10" step="1" value={tenure} onChange={function (e) { setTenure(Number(e.target.value)); }} className="w-full accent-indigo-600 h-1.5 cursor-pointer" /></div>
            </div>
            <div className="grid grid-cols-3 gap-2 pt-2 border-t border-indigo-200 dark:border-indigo-800">
                <div className="text-center"><p className="text-[10px] text-slate-500 uppercase">Monthly EMI</p><p className="text-sm font-bold text-indigo-600">{fmt(emiVal)}</p></div>
                <div className="text-center border-x border-indigo-200 dark:border-indigo-800"><p className="text-[10px] text-slate-500 uppercase">Total Interest</p><p className="text-sm font-bold text-slate-700 dark:text-slate-300">{fmt(ti)}</p></div>
                <div className="text-center"><p className="text-[10px] text-slate-500 uppercase">Total Payable</p><p className="text-sm font-bold text-slate-700 dark:text-slate-300">{fmt(tp)}</p></div>
            </div>
        </div>
    );
}

function ProgressBar({ steps, current }) {
    var pct = steps.length > 1 ? Math.round((current / (steps.length - 1)) * 100) : 100;
    return (
        <div className="w-full">
            <div className="flex items-start justify-between mb-6">
                {steps.map(function (step, i) {
                    var done = i < current, active = i === current;
                    var cc = done ? 'bg-indigo-500 border-indigo-500 text-white' : active ? 'bg-white dark:bg-slate-900 border-indigo-500 text-indigo-600' : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-400';
                    var lc = active ? 'text-indigo-600 dark:text-indigo-400' : done ? 'text-slate-600 dark:text-slate-400' : 'text-slate-400';
                    return (
                        <div key={i} className="flex-1 flex flex-col items-center relative">
                            {i < steps.length - 1 ? <div className={'absolute top-4 left-1/2 w-full h-0.5 ' + (done ? 'bg-indigo-500' : 'bg-slate-200 dark:bg-slate-700')} /> : null}
                            <div className={'relative z-10 h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ' + cc}>
                                {done ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
                            </div>
                            <span className={'mt-2 text-[10px] font-medium text-center leading-tight max-w-[70px] ' + lc}>{step}</span>
                        </div>
                    );
                })}
            </div>
            <div className="h-1.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all duration-700" style={{ width: pct + '%' }} />
            </div>
            <div className="flex justify-between mt-1">
                <span className="text-[10px] text-slate-400">Step {current + 1} of {steps.length}</span>
                <span className="text-[10px] text-indigo-600 font-semibold">{pct}% Complete</span>
            </div>
        </div>
    );
}

var STEPS = ['Basic Details', 'Business Details', 'Documents', 'Review & Submit'];

var BusinessLoanForm = function () {
    var sb = useState({ fullName: '', mobile: '', email: '', dob: '', pan: '', aadhaar: '', employmentType: '' });
    var basic = sb[0]; var setBasic = sb[1];
    var sbz = useState({ businessName: '', businessType: '', vintage: '', turnover: '', gst: '', loanAmount: '' });
    var biz = sbz[0]; var setBiz = sbz[1];
    var sf = useState({}); var files = sf[0]; var setFiles = sf[1];
    var sc = useState(0); var current = sc[0]; var setCurrent = sc[1];
    var ss = useState(false); var submitted = ss[0]; var setSubmitted = ss[1];

    function upd(setter) { return function (k, v) { setter(function (p) { var n = Object.assign({}, p); n[k] = v; return n; }); }; }
    function updFile(k, f) { setFiles(function (p) { var n = Object.assign({}, p); n[k] = f; return n; }); }
    var ub = upd(setBasic), ubz = upd(setBiz);

    var isLast = current === STEPS.length - 1;
    function handleNext() { if (isLast) { setSubmitted(true); return; } setCurrent(function (p) { return p + 1; }); window.scrollTo({ top: 0, behavior: 'smooth' }); }
    function handleBack() { setCurrent(function (p) { return p - 1; }); window.scrollTo({ top: 0, behavior: 'smooth' }); }

    function cB(k) { return { value: basic[k], onChange: function (e) { ub(k, e.target.value); } }; }
    function cBz(k) { return { value: biz[k], onChange: function (e) { ubz(k, e.target.value); } }; }

    var docs = [
        { key: 'panCard', label: 'PAN Card', accept: '.pdf,.jpg,.jpeg,.png' },
        { key: 'aadhaarCard', label: 'Aadhaar Card', accept: '.pdf,.jpg,.jpeg,.png' },
        { key: 'gstCert', label: 'GST Certificate', accept: '.pdf,.jpg,.jpeg,.png' },
        { key: 'itr', label: 'ITR (Last 2 Years)', accept: '.pdf' },
        { key: 'bankStatement', label: 'Bank Statement (6 Months)', accept: '.pdf' },
        { key: 'bizProof', label: 'Business Registration Proof', accept: '.pdf,.jpg,.jpeg,.png' },
    ];

    var stepContent;
    if (current === 0) {
        stepContent = (
            <div className="space-y-5">
                <div className="grid gap-5 sm:grid-cols-2">
                    <FormInput id="fn" label="Full Name" placeholder="Amit Shah" required {...cB('fullName')} />
                    <FormInput id="mo" label="Mobile Number" type="tel" placeholder="+91 98765 43210" required {...cB('mobile')} />
                    <FormInput id="em" label="Email" type="email" placeholder="amit@business.com" required {...cB('email')} />
                    <FormInput id="db" label="Date of Birth" type="date" required {...cB('dob')} />
                    <FormInput id="pa" label="PAN Number" placeholder="ABCDE1234F" required {...cB('pan')} />
                    <FormInput id="aa" label="Aadhaar Number" placeholder="1234 5678 9012" required {...cB('aadhaar')} />
                </div>
                <FormSelect id="et" label="Employment Type" required options={[{ value: 'salaried', label: 'Salaried' }, { value: 'self_employed', label: 'Self Employed' }]} {...cB('employmentType')} />
            </div>
        );
    } else if (current === 1) {
        stepContent = (
            <div className="grid gap-5 sm:grid-cols-2">
                <FormInput id="bn" label="Business Name" placeholder="Shah Enterprises Pvt. Ltd." required {...cBz('businessName')} />
                <FormSelect id="bt" label="Business Type" options={[{ value: 'prop', label: 'Proprietorship' }, { value: 'part', label: 'Partnership' }, { value: 'pvt', label: 'Pvt. Ltd.' }, { value: 'llp', label: 'LLP' }]} {...cBz('businessType')} />
                <FormInput id="vt" label="Business Vintage (Years)" type="number" placeholder="5" required {...cBz('vintage')} />
                <FormInput id="to" label="Annual Turnover (Rs.)" type="number" placeholder="5000000" required {...cBz('turnover')} />
                <FormInput id="gs" label="GST Number" placeholder="22AAAAA0000A1Z5" {...cBz('gst')} />
                <FormInput id="la" label="Loan Amount Required (Rs.)" type="number" placeholder="2000000" required {...cBz('loanAmount')} />
            </div>
        );
    } else if (current === 2) {
        stepContent = (
            <div className="grid sm:grid-cols-2 gap-4">
                {docs.map(function (d) { return <FileUploadField key={d.key} label={d.label} accept={d.accept} file={files[d.key]} onChange={function (e) { updFile(d.key, e.target.files[0]); }} />; })}
            </div>
        );
    } else {
        var rows = [['Full Name', basic.fullName], ['Mobile', basic.mobile], ['Email', basic.email], ['PAN', basic.pan], ['Employment', basic.employmentType], ['Business Name', biz.businessName], ['Business Type', biz.businessType], ['Vintage', biz.vintage ? biz.vintage + ' yrs' : '-'], ['Annual Turnover', biz.turnover ? 'Rs. ' + Number(biz.turnover).toLocaleString('en-IN') : '-'], ['GST', biz.gst || '-'], ['Loan Amount', biz.loanAmount ? 'Rs. ' + Number(biz.loanAmount).toLocaleString('en-IN') : '-']];
        stepContent = (
            <div className="space-y-4">
                <p className="text-sm text-slate-500 dark:text-slate-400">Please review your details before submitting.</p>
                <div className="rounded-xl border border-slate-100 dark:border-slate-800 overflow-hidden"><table className="w-full text-sm"><tbody>{rows.map(function (row, i) { return (<tr key={row[0]} className={i % 2 === 0 ? 'bg-slate-50 dark:bg-slate-800/40' : 'bg-white dark:bg-slate-900'}><td className="px-4 py-2.5 font-medium text-slate-600 dark:text-slate-400 w-1/2">{row[0]}</td><td className="px-4 py-2.5 text-slate-900 dark:text-slate-100">{row[1] || '-'}</td></tr>); })}</tbody></table></div>
                <p className="text-xs text-slate-400">By submitting you agree to our Terms and Privacy Policy.</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-950 pb-24 -mt-16">
            <div className="bg-gradient-to-r from-slate-900 to-indigo-950 text-white pt-32 pb-16">
                <div className="container mx-auto px-4 max-w-6xl">
                    <div className="flex items-center gap-4 mb-4">
                        <div className="h-12 w-12 rounded-2xl bg-indigo-500/20 flex items-center justify-center"><Briefcase className="h-6 w-6 text-indigo-400" /></div>
                        <div>
                            <p className="text-indigo-400 text-sm font-medium uppercase tracking-widest">Apply Online</p>
                            <h1 className="text-3xl md:text-4xl font-bold font-['Plus_Jakarta_Sans']">Business Loan Application</h1>
                        </div>
                    </div>
                    <p className="text-slate-300 max-w-xl ml-16">Scale your operations. Interest starting from <strong className="text-indigo-400">10.5% p.a.</strong></p>
                </div>
            </div>
            <div className="container mx-auto px-4 max-w-6xl mt-10">
                {submitted ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center space-y-5">
                        <div className="h-24 w-24 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center animate-bounce"><CheckCircle2 className="h-12 w-12 text-indigo-500" /></div>
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white font-['Plus_Jakarta_Sans']">Application Submitted!</h2>
                        <p className="text-slate-600 dark:text-slate-400 max-w-sm">Your <strong>Business Loan</strong> application has been received. We will reach out within 24 to 48 hours.</p>
                        <div className="flex gap-3"><Button asChild className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl"><a href="/loan-products">Explore More</a></Button><Button variant="outline" asChild className="rounded-xl"><a href="/">Home</a></Button></div>
                    </div>
                ) : (
                    <div className="grid lg:grid-cols-3 gap-8">
                        <div className="lg:col-span-2 space-y-8">
                            <ProgressBar steps={STEPS} current={current} />
                            <div className="rounded-2xl border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-md p-6 md:p-8 space-y-6">
                                <div className="border-b border-slate-100 dark:border-slate-800 pb-4">
                                    <h3 className="text-lg font-bold text-slate-900 dark:text-white font-['Plus_Jakarta_Sans']">{STEPS[current]}</h3>
                                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Fields marked with <span className="text-rose-500">*</span> are required.</p>
                                </div>
                                {stepContent}
                                <div className="flex items-center justify-between pt-4 border-t border-slate-100 dark:border-slate-800">
                                    <Button variant="outline" onClick={handleBack} disabled={current === 0} className="rounded-xl gap-1.5"><ChevronLeft className="h-4 w-4" />Back</Button>
                                    <Button onClick={handleNext} className="rounded-xl gap-1.5 bg-slate-900 hover:bg-slate-800 dark:bg-indigo-600 dark:hover:bg-indigo-700 text-white px-6">
                                        {isLast ? 'Submit Application' : 'Continue'}
                                        {isLast ? <CheckCircle2 className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                    </Button>
                                </div>
                            </div>
                        </div>
                        <div className="space-y-6">
                            <EMICalc />
                            <div className="rounded-2xl border border-amber-100 dark:border-amber-900/40 bg-amber-50/40 dark:bg-amber-900/10 p-5 space-y-2">
                                <h4 className="text-sm font-semibold text-amber-700 dark:text-amber-400">Need Help?</h4>
                                <p className="text-xs text-slate-600 dark:text-slate-400">Speak to our expert for personalised guidance.</p>
                                <p className="text-sm font-bold text-amber-700 dark:text-amber-400">1800-XXX-XXXX</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BusinessLoanForm;
